import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import { useScrollShadow } from 'hooks/useScrollShadow';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { HelpBox } from 'components/v2/help-boxes';
import { ProcessStatus } from 'constants/constants';
import { Command } from 'constants/constants';
import { DeveloperCompanyModel } from 'services/EpdClient';
import styled from 'styled-components';
import { Option } from 'types/types';

import { DialogContentContainer } from '../../../../pcr-selector/styled';
import { ScrollShadowDialog } from 'styles/Styles.styled';
import InviteExternalPractitionerTextInput from './InviteExternalPractitionerTextInput';
import LcaCompaniesList from './LcaCompaniesList';

type TProps = {
  epdId: string;
  epdVersionId: string;
  companyId?: string | undefined;
  selectedPractitioners?: Option[] | undefined;
  companyPractitioners?: Option[] | undefined;
  filteredEpdDeveloperCompanies: DeveloperCompanyModel[];
  inquiryStatus: [ProcessStatus, Command];
  isOpened: boolean;
  onHide: any;
  onChange: any;
  onAddPractitioner: any;
  onRequestCollaboration: any;
  handleSearchVerifierCompanies: any;
  onChangePractitionerCountry: any;
  contentStyle?: any;
  selectedEpdDeveloperCompany?: DeveloperCompanyModel | undefined;
  setSelectedEpdDeveloperCompany: any;
  countries?: Option[] | undefined;
  selectedCountry?: Option | undefined;
};

const LcaPractitionersSelectorDialog = ({
  epdId,
  epdVersionId,
  companyId,
  selectedPractitioners,
  companyPractitioners,
  filteredEpdDeveloperCompanies,
  inquiryStatus,
  isOpened,
  onHide,
  onChange,
  onAddPractitioner,
  onRequestCollaboration,
  handleSearchVerifierCompanies,
  onChangePractitionerCountry,
  contentStyle,
  selectedEpdDeveloperCompany,
  setSelectedEpdDeveloperCompany,
  countries,
  selectedCountry,
  ...props
}: TProps) => {
  const { isScrolled, contentRef } = useScrollShadow();

  return (
    <StyledDialog
      resizable={false}
      $isScrolled={isScrolled}
      header="LCA Practitioner"
      footer={
        <DialogFooter>
          <HelpBox>
            Can't find your LCA Practitioner in the list? Enter their email below to send an invitation to the EPD Portal.
            Once they register, you will be able to share access to your EPD.
          </HelpBox>
          <InviteExternalPractitionerWrapper>
            <InviteExternalPractitionerTextInput companyId={companyId} />
          </InviteExternalPractitionerWrapper>
        </DialogFooter>
      }
      visible={isOpened}
      style={{ width: '35rem' }}
      contentStyle={!!filteredEpdDeveloperCompanies.length ? {} : { overflowY: 'initial' }}
      onHide={onHide}
      {...props}
    >
      <DialogContentContainer ref={contentRef}>
        <LcaDialogHelpBox>
          <div>
            You can add LCA Practitioners to your project in two ways:
            <ul>
              <li>
                If you have already agreed to work with a practitioner from the list below, click <b>"Add practitioner"</b>
                &nbsp; to assign them directly to your EPD.
              </li>
              <li>
                If you need to discuss collaboration first, select one or more practitioners and click{' '}
                <b>"Request collaboration"</b>&nbsp; to notify them of your interest in collaborating on LCA/EPD development.
                Once confirmed, use <b>"Add practitioner"</b>&nbsp; as described above.
              </li>
            </ul>
          </div>
        </LcaDialogHelpBox>
        <CompilerSelectControlledComponent
          label="Country"
          name="verifierCountry"
          options={countries}
          value={selectedCountry}
          onChanged={onChangePractitionerCountry}
          placeholder="Select"
          isClearable={true}
        />
        <DialogContentRow>
          <WizardTextInputControlledComponent
            label="Organisation name or e-mail address"
            value={undefined}
            placeholder="Type here to search"
            onChange={handleSearchVerifierCompanies}
            onChanged={() => {}}
            name="nameOrEmail"
            maxLength={150}
          />
        </DialogContentRow>
        <LcaCompaniesList
          selectedPractitioners={selectedPractitioners}
          companyPractitioners={companyPractitioners}
          companies={filteredEpdDeveloperCompanies}
          selectedCompany={selectedEpdDeveloperCompany}
          onSelectCompany={setSelectedEpdDeveloperCompany}
          onRequestCollaboration={onRequestCollaboration}
          onAddPractitioner={onAddPractitioner}
          buttonsAreDisabled={
            (inquiryStatus[0] === ProcessStatus.Fetching && inquiryStatus[1] === Command.Inquiry) ||
            (inquiryStatus[0] === ProcessStatus.Fetching && inquiryStatus[1] === Command.Connect)
          }
        />
      </DialogContentContainer>
    </StyledDialog>
  );
};

const StyledDialog = styled(ScrollShadowDialog)`
  .p-dialog-header-close svg path {
    fill: ${(props) => props.theme.colors.primaryBlack};
  }

  && .p-dialog-footer {
    padding-bottom: 30px;
    height: auto;
  }
`;

const DialogFooter = styled.div`
  padding-left: 1rem;
  padding-right: 1rem;
`;

const InviteExternalPractitionerWrapper = styled.div`
  margin-top: 8px;
`;

const DialogContentRow = styled.div`
  margin-top: 1rem;
`;

const LcaDialogHelpBox = styled(HelpBox)`
  ul {
    margin: 0;
    padding-left: 30px;
  }
`;

export default LcaPractitionersSelectorDialog;
